import { LocaleType } from "../../../constants/constants";
import React, { useState } from "react";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useUserSessionContext, useUserSessionContextDispatcher } from "../../context/application-context/user-session-context-provider";

import FlagIconGB from "../../../common/component/img/flag_gb.svg";
import FlagIconFR from "../../../common/component/img/flag_fr.svg";
import FlagIconES from "../../../common/component/img/flag_es.svg";
import FlagIconIT from "../../../common/component/img/flag_it.svg";
import FlagIconPL from "../../../common/component/img/flag_pl.svg";
import FlagIconDE from "../../../common/component/img/flag_de.svg";
import { context } from "../../../context/context";

import "./style.scss";

interface ILanguageSelectorMenuProps {
    anchor: HTMLElement;
    onChange: (locale: LocaleType) => void;
    onClose: () => void;
}

interface ILanguageSelectorItemProps {
    locale: LocaleType;
    onClick: (locale: LocaleType) => void;
}

export const LanguageSelector = ({ withoutContext }: { withoutContext?: boolean }) => {
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<HTMLElement | null>(null);

    const applicationContext = useUserSessionContext();
    const applicationContextDispatcher = useUserSessionContextDispatcher();

    const handleItemClick = async (locale: LocaleType) => {
        applicationContextDispatcher({
            action: "change-locale",
            locale,
        });
        if (!withoutContext) {
            await context.updateLocale(locale.toString());
        }
        setLanguageMenuAnchor(null);
    };

    return <>
        <Button variant={"outlined"}
                data-testid={"locale-select"}
                className={"language-selector__button"}
                color={languageMenuAnchor === null ? "inherit" : "secondary"}
                onClick={event => setLanguageMenuAnchor(event.currentTarget)}
                startIcon={<img className={"language-selector__flag"}
                                data-testid={`locale-${applicationContext.locale}-selected`}
                                src={flagFromLocale(applicationContext.locale)}/>}
                endIcon={<KeyboardArrowDown />}
                size={"large"}>
            {labelShortFromLocale(applicationContext.locale)}
        </Button>
        {languageMenuAnchor && <LanguageSelectorMenu anchor={languageMenuAnchor}
                                                     onChange={locale => handleItemClick(locale)}
                                                     onClose={() => setLanguageMenuAnchor(null)} />}
    </>;
};

const LanguageSelectorMenu = (props: ILanguageSelectorMenuProps) => {
    return <Menu open={Boolean(props.anchor)}
                 className={"language-menu"}
                 data-testid={"language-menu"}
                 elevation={0}
                 anchorEl={props.anchor}
                 anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                 onClose={props.onClose}>
        <LanguageSelectorMenuItem key={"en-GB"} locale={"en-GB"} onClick={locale => props.onChange(locale)} />
        <LanguageSelectorMenuItem key={"fr-FR"} locale={"fr-FR"} onClick={locale => props.onChange(locale)} />
        <LanguageSelectorMenuItem key={"es-ES"} locale={"es-ES"} onClick={locale => props.onChange(locale)} />
        <LanguageSelectorMenuItem key={"it-IT"} locale={"it-IT"} onClick={locale => props.onChange(locale)} />
        <LanguageSelectorMenuItem key={"de-DE"} locale={"de-DE"} onClick={locale => props.onChange(locale)} />
        <LanguageSelectorMenuItem key={"pl-PL"} locale={"pl-PL"} onClick={locale => props.onChange(locale)} />
    </Menu>;
};

const LanguageSelectorMenuItem = (props: ILanguageSelectorItemProps) => {
    return <MenuItem onClick={() => props.onClick(props.locale)}
                     data-testid={`locale-${props.locale}`}
                     className={"language-menu__item"}>
        <ListItemIcon>
            <img className={"language-menu__flag"} src={flagFromLocale(props.locale)} />
        </ListItemIcon>
        <ListItemText className={"language-menu__label"} primary={labelFromLocale(props.locale)} />
    </MenuItem>;
};

const flagFromLocale = (locale: LocaleType) => {
    switch (locale) {
        case "fr-FR":
            return FlagIconFR;
        case "en-GB":
            return FlagIconGB;
        case "es-ES":
            return FlagIconES;
        case "it-IT":
            return FlagIconIT;
        case "de-DE":
            return FlagIconDE;
        case "pl-PL":
            return FlagIconPL;
        default:
            return "";
    }
};

const labelFromLocale = (locale: LocaleType) => {
    switch (locale) {
        case "fr-FR":
            return "Français";
        case "en-GB":
            return "English";
        case "es-ES":
            return "Español";
        case "it-IT":
            return "Italiano";
        case "de-DE":
            return "Deutsch";
        case "pl-PL":
            return "Polski";
        default:
            return "English";
    }
};

const labelShortFromLocale = (locale: LocaleType) => {
    switch (locale) {
        case "fr-FR":
            return "FR";
        case "en-GB":
            return "EN";
        case "es-ES":
            return "ES";
        case "it-IT":
            return "IT";
        case "de-DE":
            return "DE";
        case "pl-PL":
            return "PL";
        default:
            return "EN";
    }
};
